export const SUPPORTED_LOCALES = [
    'ar',
    'en',
    'es',
    'fil',
    'id',
    'ja',
    'pt',
    'ru',
    'th',
    'tr',
    'fr',
    'de',
    'it',
    'pl',
    'vi',
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';
export const LOCALE_STORAGE_KEY = 'lng';

const supportedLocaleSet = new Set<string>(SUPPORTED_LOCALES);

export function isSupportedLocale(
    value: string | null | undefined,
): value is Locale {
    return !!value && supportedLocaleSet.has(value);
}

export function getPrimaryLanguageTag(
    language: string | null | undefined,
): string {
    if (!language) return DEFAULT_LOCALE;
    return language.split('-')[0].toLowerCase();
}

export function getLocaleFromPath(pathname: string): Locale | null {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    const maybeLocale = segments[0]?.toLowerCase();
    return isSupportedLocale(maybeLocale) ? maybeLocale : null;
}

export function stripLocalePrefix(pathname: string): string {
    const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const segments = normalizedPath.split('/').filter(Boolean);

    if (segments.length === 0) {
        return '/';
    }

    if (!isSupportedLocale(segments[0])) {
        return normalizedPath;
    }

    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
}

export function localizePath(pathname: string, locale: Locale): string {
    const unlocalizedPath = stripLocalePrefix(pathname);
    return unlocalizedPath === '/'
        ? `/${locale}`
        : `/${locale}${unlocalizedPath}`;
}

export function getSavedLocale(): Locale | null {
    try {
        const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
        return isSupportedLocale(stored) ? stored : null;
    } catch {
        return null;
    }
}

export function detectBrowserLocale(): Locale {
    const primaryTag = getPrimaryLanguageTag(window.navigator.language);
    const browserLocale =
        primaryTag === 'tl' ? 'fil' : primaryTag === 'in' ? 'id' : primaryTag;
    return isSupportedLocale(browserLocale) ? browserLocale : DEFAULT_LOCALE;
}

export function resolveLocaleForMissingPath(): Locale {
    const savedLocale = getSavedLocale();
    if (savedLocale) return savedLocale;
    return detectBrowserLocale();
}

export function buildLocalizedPathWithQueryAndHash(
    pathname: string,
    search: string,
    hash: string,
    locale: Locale,
): string {
    const localizedPath = localizePath(pathname, locale);
    return `${localizedPath}${search}${hash}`;
}
